import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ActionButtons, { EditButton, DeleteButton } from '../../components/ActionButtons';
import DataTable from '../../components/DataTable';
import { apiRequest } from '../../services/api';
import UsageInfo from '../../components/UsageInfo';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import ModalShell from '../../components/ModalShell';

const TIP_STIL = {
  'Ön büro': 'bg-blue-50 text-blue-600 border border-blue-100 shadow-sm',
  'Housekeeping': 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-sm',
  'Muhasebe': 'bg-violet-50 text-violet-600 border border-violet-100 shadow-sm',
  'Yönetici': 'bg-amber-50 text-amber-600 border border-amber-100 shadow-sm',
  'Genel': 'bg-gray-50 text-gray-500 border border-gray-200 shadow-sm',
};
const TIP_IKON = {
  'Ön büro': '🏨', 'Housekeeping': '🧹', 'Muhasebe': '💼',
  'Yönetici': '👑', 'Genel': '👤', 'Güvenlik': '🔒', 'Restorant': '🍽️',
};
const AVATAR_RENK = {
  'Ön büro': 'from-blue-500 to-indigo-600 shadow-blue-200', 
  'Housekeeping': 'from-emerald-500 to-teal-600 shadow-emerald-200',
  'Muhasebe': 'from-violet-500 to-purple-600 shadow-violet-200', 
  'Yönetici': 'from-amber-400 to-orange-600 shadow-amber-200',
  'Güvenlik': 'from-red-500 to-rose-600 shadow-red-200', 
  'Restorant': 'from-orange-500 to-red-600 shadow-orange-200',
  'Genel': 'from-slate-400 to-slate-600 shadow-slate-200',
};

function FormHata({ mesaj }) {
  if (!mesaj) return null;
  return <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mt-2 flex items-center gap-1 animate-shake">⚠️ {mesaj}</p>;
}

function Avatar({ name, tip, size = 'md' }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const sz = size === 'md' ? 'w-12 h-12 text-sm' : 'w-10 h-10 text-xs';
  return (
    <div className={`${sz} rounded-2xl bg-gradient-to-br ${AVATAR_RENK[tip] || AVATAR_RENK['Genel']} flex items-center justify-center text-white font-black flex-shrink-0 shadow-lg border-2 border-white ring-4 ring-gray-50 transition-transform group-hover:scale-110 duration-500 transform-gpu`}>
      {initials}
    </div>
  );
}

function RolSecimGrid({ roller, seciliIds, onChange }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 transform-gpu">
      {(roller || []).map(rol => {
        const secili = seciliIds.includes(rol.rolId);
        return (
          <label key={rol.rolId}
            className={`flex items-start gap-3 p-4 rounded-2xl border-2 transition-all cursor-pointer group leading-none
              ${secili 
                ? 'border-indigo-400 bg-indigo-50/50 shadow-xl shadow-indigo-100/50 scale-[1.02]' 
                : 'border-gray-50 bg-gray-50/30 hover:border-gray-200 hover:bg-white'}`}>
            <div className={`w-5 h-5 mt-0.5 rounded-lg flex-shrink-0 border-2 flex items-center justify-center transition-all ${secili ? 'bg-indigo-600 border-indigo-600' : 'border-gray-200 bg-white group-hover:border-indigo-300'}`}
              onClick={(e) => { e.preventDefault(); onChange(rol.rolId, !secili); }}>
              {secili && <span className="text-white text-[10px] font-black leading-none">✓</span>}
            </div>
            <div className="min-w-0 text-left leading-none">
              <div className="text-[11px] font-black text-gray-800 uppercase tracking-tight leading-none group-hover:text-indigo-600">{rol.ad}</div>
              {rol.aciklama && <div className="text-[9px] font-bold text-gray-400 leading-tight mt-1.5 truncate opacity-70 group-hover:opacity-100">{rol.aciklama}</div>}
            </div>
          </label>
        );
      })}
    </div>
  );
}

function PersonelFormModal({ personel, personelTipleri, roller, onKaydet, onKapat, kaydediliyor, hata }) {
  const yeni = !personel;
  const [form, setForm] = useState({
    personelId: personel?.personelId ?? null,
    tip: personel?.tip ?? (personelTipleri[0] || 'Genel'),
    adSoyad: personel?.adSoyad ?? '',
    kullaniciAdi: personel?.kullaniciAdi ?? '',
    sifre: '',
    email: personel?.email ?? '',
    telefon: personel?.telefon ?? '',
    aktifMi: personel?.aktifMi ?? true,
    rolIdleri: personel?.rolIdleri ?? [],
    calismaBaslangic: personel?.calismaBaslangic ?? '',
    calismaBitis: personel?.calismaBitis ?? '',
    calismaGunleri: personel?.calismaGunleri ?? '',
    firmaId: personel?.firmaId ?? null,
  });
  const [firmalar, setFirmalar] = useState([]);
  useEffect(function() {
    apiRequest('firmalar').then(function(d) { setFirmalar(d || []); }).catch(function(){});
  }, []);

  const [hatalar, setHatalar] = useState({});
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setHatalar(p => ({ ...p, [k]: null })); };

  const GUNLER = [
    { no: 1, kisa: 'PZT' }, { no: 2, kisa: 'SAL' }, { no: 3, kisa: 'ÇRŞ' },
    { no: 4, kisa: 'PER' }, { no: 5, kisa: 'CUM' }, { no: 6, kisa: 'CMT' }, { no: 7, kisa: 'PAZ' },
  ];
  const seciliGunler = (form.calismaGunleri || '').split(',').map(x => parseInt(x)).filter(Boolean);
  
  const toggleGun = (no) => {
    const y = seciliGunler.includes(no) ? seciliGunler.filter(g => g !== no) : [...seciliGunler, no].sort((a, b) => a - b);
    set('calismaGunleri', y.join(','));
  };

  const INP = (h) => `w-full border-2 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 transition-all bg-white focus:outline-none ${h ? 'border-rose-400 bg-rose-50 focus:border-rose-500 shadow-rose-50 shadow-inner' : 'border-gray-100 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-50 shadow-sm'}`;

  const handleKaydet = () => {
    if (!form.adSoyad.trim()) { setHatalar({ adSoyad: 'Ad Soyad zorunludur.' }); return; }
    if (!form.kullaniciAdi.trim()) { setHatalar({ kullaniciAdi: 'Kullanıcı adı zorunludur.' }); return; }
    
    // Baş harfleri büyük yap (Muaz Ceyhan formatı)
    const formatliAd = form.adSoyad.toLocaleLowerCase('tr-TR').split(' ').map(s => s.charAt(0).toLocaleUpperCase('tr-TR') + s.slice(1)).join(' ');
    onKaydet({ ...form, adSoyad: formatliAd });
  };

  return (
    <ModalShell
      icon="👥"
      iconColor="from-indigo-600 to-violet-700"
      title={yeni ? 'Yeni Personel Tanımla' : 'Personel Kartı Güncelle'}
      subtitle="Hesap yönetimi ve yetkilendirmeler"
      onClose={onKapat}
      size="xl"
      footer={
        <>
          <button onClick={onKapat} className="px-8 py-3.5 rounded-2xl border-2 border-gray-100 text-xs font-black text-gray-400 hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95">İPTAL</button>
          <button onClick={handleKaydet} disabled={kaydediliyor}
            className="px-10 py-3.5 bg-gradient-to-r from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 disabled:opacity-60 text-white rounded-2xl text-xs font-black shadow-xl shadow-indigo-200 transition-all hover:-translate-y-1 active:translate-y-0 tracking-widest uppercase">
            {kaydediliyor ? '⌛ İŞLEM...' : '💾 PERSONELİ KAYDET'}
          </button>
        </>
      }
    >
      {hata && <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl px-5 py-4 text-xs text-rose-600 font-black uppercase tracking-widest animate-shake leading-none">⚠️ {hata}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 leading-none">
         <div className="space-y-6 leading-none">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 leading-none">📋 KİMLİK & HESAP</p>
            <div className="grid grid-cols-1 gap-6 leading-none">
               <div>
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">AD SOYAD *</label>
                 <input value={form.adSoyad} onChange={e => set('adSoyad', e.target.value)} className={INP(hatalar.adSoyad)} placeholder="ÖRN: MEHMET YILMAZ" />
               </div>
               <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">KULLANICI ADI *</label>
                    <input value={form.kullaniciAdi} onChange={e => set('kullaniciAdi', e.target.value)} className={INP(hatalar.kullaniciAdi)} placeholder="m_yilmaz" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">POZİSYON</label>
                    <select value={form.tip} onChange={e => set('tip', e.target.value)} className={INP(false) + ' cursor-pointer'}>
                      {personelTipleri.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
               </div>
               <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">ŞİFRE {yeni ? '*' : '(BOŞSA DEĞİŞMEZ)'}</label>
                  <input type="password" value={form.sifre} onChange={e => set('sifre', e.target.value)} className={INP(false)} placeholder="••••••••" />
               </div>
               <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">E-POSTA</label>
                    <input value={form.email} onChange={e => set('email', e.target.value)} className={INP(false)} placeholder="MAIL@ADRES.COM" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">TELEFON</label>
                    <input value={form.telefon} onChange={e => set('telefon', e.target.value)} className={INP(false)} placeholder="05XX XXX XX XX" />
                  </div>
               </div>
               <div>
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">BAĞLI FİRMA <span className="text-gray-300 normal-case tracking-normal font-bold">(Firma yetkilisi için)</span></label>
                 <select value={form.firmaId ?? ''} onChange={e => set('firmaId', e.target.value ? parseInt(e.target.value) : null)} className={INP(false) + ' cursor-pointer'}>
                   <option value="">— Bağlı Firma Yok —</option>
                   {firmalar.map(f => <option key={f.firmaId} value={f.firmaId}>{f.ad}</option>)}
                 </select>
               </div>
            </div>
         </div>

         <div className="space-y-6 leading-none">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 leading-none">🕒 ÇALIŞMA TAKVİMİ</p>
            <div className="bg-indigo-50/50 border-2 border-indigo-100 rounded-[2.5rem] p-7 space-y-7 leading-none shadow-inner">
               <div className="grid grid-cols-2 gap-4">
                  <div>
                     <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-2.5">GİRİŞ SAATİ</label>
                     <input type="time" value={form.calismaBaslangic} onChange={e => set('calismaBaslangic', e.target.value)} className={INP(false) + ' h-[50px] text-center'} />
                  </div>
                  <div>
                     <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-2.5">ÇIKIŞ SAATİ</label>
                     <input type="time" value={form.calismaBitis} onChange={e => set('calismaBitis', e.target.value)} className={INP(false) + ' h-[50px] text-center'} />
                  </div>
               </div>
               <div>
                  <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-4 ml-1">ÇALIŞMA GÜNLERİ</label>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                    {GUNLER.map(g => (
                      <button key={g.no} type="button" onClick={() => toggleGun(g.no)}
                        className={`h-11 rounded-xl text-[9px] font-black transition-all border-2 transform-gpu leading-none active:scale-90 ${seciliGunler.includes(g.no) ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100' : 'bg-white border-indigo-100 text-indigo-700 hover:border-indigo-300'}`}>
                        {g.kisa}
                      </button>
                    ))}
                  </div>
               </div>
               <div className="bg-indigo-600 rounded-2xl p-4 text-white leading-relaxed text-[10px] font-bold uppercase tracking-tight shadow-xl shadow-indigo-100">
                  💡 MESAİ SAATLERİ VE GÜNLERİ, SADECE BU PERSONELİN GÖREV HATIRLATMALARI ALACAĞI ZAMANI BELİRLER.
               </div>
            </div>
         </div>

         <div className="md:col-span-2 space-y-6 leading-none">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 leading-none text-left">📂 ERİŞİM ROLLERİ</p>
            <RolSecimGrid roller={roller} seciliIds={form.rolIdleri} onChange={(id, s) => set('rolIdleri', s ? [...form.rolIdleri, id] : form.rolIdleri.filter(x => x !== id))} />
         </div>
      </div>
    </ModalShell>
  );
}

export default function PersonelBirimi() {
  const [personeller, setPersoneller] = useState([]);
  const [personelTipleri, setPersonelTipleri] = useState([]);
  const [roller, setRoller] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [modal, setModal] = useState(null);
  const [silOnay, setSilOnay] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [tipFiltre, setTipFiltre] = useState('Tümü');
  const [aktifFiltre, setAktifFiltre] = useState('Tümü');
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [modalHata, setModalHata] = useState('');

  const veriYukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const [p, t, r] = await Promise.all([apiRequest('personeller'), apiRequest('personeltipleri'), apiRequest('roller/aktif')]);
      setPersoneller(p || []);
      setPersonelTipleri((t || []).filter(x => x.aktifMi).map(x => x.ad));
      setRoller(r || []);
    } catch { /* sessiz */ }
    setYukleniyor(false);
  }, []);

  useEffect(() => { veriYukle(); }, [veriYukle]);

  async function handleDuzenleClick(p) {
    setModalHata('');
    try { const d = await apiRequest(`personeller/${p.personelId}`); setModal(d); } catch { setModal(p); }
  }

  async function handleKaydet(form) {
    setKaydediliyor(true); setModalHata('');
    try {
      if (form.personelId) await apiRequest('personeller', 'PUT', form);
      else await apiRequest('personeller', 'POST', form);
      await veriYukle(); setModal(null);
    } catch (e) { setModalHata(e?.message || 'Hata oluştu.'); }
    setKaydediliyor(false);
  }

  async function handleSil(id) { await apiRequest(`personeller/${id}`, 'DELETE'); yukle(); setSilOnay(null); }
  const yukle = () => veriYukle();

  const filtrelenmis = useMemo(() => personeller.filter(p => {
    const a = (p.adSoyad?.toLocaleLowerCase('tr-TR') || '').includes(aramaMetni.toLocaleLowerCase('tr-TR')) || (p.kullaniciAdi?.toLocaleLowerCase('tr-TR') || '').includes(aramaMetni.toLocaleLowerCase('tr-TR'));
    const t = tipFiltre === 'Tümü' || p.tip === tipFiltre;
    const s = aktifFiltre === 'Tümü' || (aktifFiltre === 'Aktif' ? p.aktifMi : !p.aktifMi);
    return a && t && s;
  }), [personeller, aramaMetni, tipFiltre, aktifFiltre]);

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn text-left leading-none font-sans" style={{ transform: 'translateZ(0)' }}>
      <PageHeader
        icon="👥"
        iconColor="from-indigo-600 to-violet-700"
        iconShadow="shadow-indigo-200"
        title="Personel Birimi"
        subtitle="Erişim yetkileri ve personel kartları yönetimi"
      >
        <button onClick={() => { setModalHata(''); setModal('yeni'); }}
          className="group flex-shrink-0 flex items-center justify-center gap-4 bg-gradient-to-r from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 text-white px-10 py-4 rounded-2xl text-[10px] font-black shadow-2xl shadow-indigo-100 transition-all hover:-translate-y-1 active:translate-y-0 uppercase tracking-widest relative overflow-hidden leading-none transform-gpu">
          <span className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
          <span className="relative text-xl leading-none">＋</span> <span className="relative leading-none">PERSONEL EKLE</span>
        </button>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 leading-none transform-gpu">
        {[
          { label: 'TOPLAM KADRO', value: personeller.length, icon: '👥', color: 'from-gray-700 to-gray-900 shadow-gray-200' },
          { label: 'AKTİF ÇALIŞAN', value: personeller.filter(p => p.aktifMi).length, icon: '✅', color: 'from-emerald-500 to-emerald-700 shadow-emerald-100' },
          { label: 'POZİSYON SAYISI', value: personelTipleri.length, icon: '🏷️', color: 'from-indigo-500 to-violet-700 shadow-indigo-100' },
        ].map(k => (
          <div key={k.label} className={`relative overflow-hidden bg-gradient-to-br ${k.color} rounded-[2rem] p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 group leading-none`}>
            <div className="absolute -right-4 -bottom-4 text-7xl opacity-10 pointer-events-none group-hover:scale-125 transition-transform duration-500 transform-gpu leading-none">{k.icon}</div>
            <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1 leading-none text-left">{k.label}</p>
            <p className="text-xl md:text-2xl font-black tracking-tight leading-none text-left">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden min-h-[400px] leading-none transform-gpu transition-shadow duration-500 hover:shadow-2xl hover:shadow-indigo-100/30">
        <div className="px-6 md:px-10 py-6 border-b-2 border-gray-100 bg-gray-50/20 flex flex-col xl:flex-row xl:items-center gap-6 leading-none">
          <div className="relative flex-1 w-full text-left leading-none">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-black leading-none">🔍</span>
            <input value={aramaMetni} onChange={e => setAramaMetni(e.target.value)} placeholder="PERSONEL ADI VEYA KULLANICI ADI ARA..."
              className="w-full pl-11 pr-4 py-3.5 text-sm border-2 border-gray-100 rounded-2xl focus:border-indigo-400 focus:outline-none bg-white transition-all shadow-inner font-bold leading-none shadow-indigo-50" />
          </div>
          <div className="flex flex-wrap lg:flex-nowrap items-center gap-3 leading-none w-full xl:w-auto">
             <select value={tipFiltre} onChange={e => setTipFiltre(e.target.value)} className="flex-1 min-w-[140px] px-5 py-3.5 border-2 border-gray-100 rounded-2xl text-[10px] font-bold uppercase tracking-widest focus:outline-none focus:border-indigo-400 leading-none bg-white cursor-pointer select-none">
                <option>Tümü</option>
                {personelTipleri.map(t => <option key={t}>{t}</option>)}
             </select>
             <select value={aktifFiltre} onChange={e => setAktifFiltre(e.target.value)} className="flex-1 min-w-[140px] px-5 py-3.5 border-2 border-gray-100 rounded-2xl text-[10px] font-bold uppercase tracking-widest focus:outline-none focus:border-indigo-400 leading-none bg-white cursor-pointer select-none">
                {['Tümü', 'Aktif', 'Pasif'].map(o => <option key={o}>{o}</option>)}
             </select>
             <button onClick={() => { setAramaMetni(''); setTipFiltre('Tümü'); setAktifFiltre('Tümü'); }} className="px-6 py-3.5 text-rose-500 font-black text-[10px] uppercase tracking-widest hover:bg-rose-50 rounded-2xl transition-all leading-none">SIFIRLA</button>
          </div>
        </div>

        {yukleniyor ? (
          <LoadingSpinner fullPage text="İnsan Kaynakları Listeleniyor..." />
        ) : (
          <>
            {/* Mobile View */}
            <div className="md:hidden divide-y divide-gray-50 leading-none">
               {filtrelenmis.length === 0 ? (
                 <EmptyState icon="👥" text="Aradığınız kriterlerde personel bulunamadı" />
               ) : filtrelenmis.map(p => (
                 <div key={p.personelId} className={`p-6 flex flex-col gap-6 hover:bg-indigo-50/20 transition-all transform-gpu group ${!p.aktifMi && 'grayscale-[0.5] opacity-60 bg-gray-50'}`}>
                    <div className="flex items-center justify-between gap-4 leading-none text-left">
                       <div className="flex items-center gap-4 min-w-0 leading-none text-left">
                          <Avatar name={p.adSoyad} tip={p.tip} />
                          <div className="min-w-0 leading-none text-left">
                             <h3 className="font-black text-gray-800 text-lg uppercase tracking-tight truncate leading-none mb-2">{p.adSoyad}</h3>
                             <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">@{p.kullaniciAdi}</p>
                             <span className={`text-[8px] font-black px-2 py-0.5 rounded-lg border uppercase tracking-widest inline-block ${TIP_STIL[p.tip] || TIP_STIL['Genel']}`}>{p.tip}</span>
                          </div>
                       </div>
                       <div className={`w-3 h-3 rounded-full flex-shrink-0 animate-pulse border-2 ${p.aktifMi ? 'bg-emerald-500 border-emerald-100' : 'bg-red-500 border-red-100'}`}></div>
                    </div>
                    <div className="flex gap-2 leading-none">
                       <EditButton size="lg" onClick={() => handleDuzenleClick(p)} />
                       <DeleteButton size="lg" onClick={() => setSilOnay(p.personelId)} />
                    </div>
                 </div>
               ))}
            </div>

            {/* Desktop View */}
            <div className="hidden md:block overflow-x-auto leading-none">
              <DataTable
                anahtarAlan="personelId"
                satirlar={filtrelenmis}
                bosIkon="👥"
                bosMetin="Kayıt bulunamadı"
                satirSinifi={p => !p.aktifMi ? 'grayscale-[0.8] opacity-60 bg-gray-50/40' : ''}
                kolonlar={[
                  {
                    anahtar: 'sira', baslik: '#', genislik: 'w-20',
                    ciz: (p, i) => <span className="font-black text-gray-300 text-xs">{i + 1}</span>,
                  },
                  {
                    anahtar: 'adSoyad', baslik: 'Personel Künyesi',
                    ciz: p => (
                      <div className="flex items-center gap-5">
                        <Avatar name={p.adSoyad} tip={p.tip} />
                        <div>
                          <h3 className="font-black text-gray-700 text-base uppercase tracking-tight mb-2">{p.adSoyad}</h3>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">@{p.kullaniciAdi}</p>
                        </div>
                      </div>
                    ),
                  },
                  {
                    anahtar: 'tip', baslik: 'Birim / Departman',
                    ciz: p => (
                      <span className={'text-[10px] font-black px-4 py-2 rounded-xl border-2 uppercase tracking-widest inline-block ' +
                        (TIP_STIL[p.tip] || TIP_STIL['Genel'])}>
                        <span className="mr-2 font-emoji">{TIP_IKON[p.tip] || '🏷️'}</span>{p.tip}
                      </span>
                    ),
                  },
                  {
                    anahtar: 'aktifMi', baslik: 'Erişim Durumu', hizala: 'orta', genislik: 'w-48',
                    ciz: p => (
                      <span className={'inline-flex items-center gap-2 text-[10px] font-black px-4 py-1.5 rounded-full border tracking-widest ' +
                        (p.aktifMi ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100')}>
                        <span className={'w-2 h-2 rounded-full ' + (p.aktifMi ? 'bg-emerald-500' : 'bg-rose-500')} />
                        {p.aktifMi ? 'AKTİF HESAP' : 'ERİŞİM YOK'}
                      </span>
                    ),
                  },
                  {
                    anahtar: 'islem', baslik: 'İşlemler', hizala: 'sag',
                    ciz: p => (
                      <ActionButtons
                        onEdit={() => handleDuzenleClick(p)}
                        onDelete={() => setSilOnay(p.personelId)} />
                    ),
                  },
                ]}
              />
            </div>
          </>
        )}
      </div>

      <UsageInfo
        color="indigo"
        description="Personel Birimi, tesisinizdeki kullanıcı hesaplarını, mesai takvimlerini ve erişim rollerini yönettiğiniz merkezi yönetim panelidir."
        scenarios={[
          { title: "Giriş Güvenliği", content: "Personelin sisteme giriş yetkisini tek tıkla pasife alabilir, mesai saatleri dışındaki erişimlerini kısıtlayabilirsiniz." },
          { title: "Çoklu Rol Atama", content: "Bir personele hem 'Resepsiyon' hem 'Yönetici' rolü atayarak yetki alanlarını hibrit şekilde kurgulayabilirsiniz." },
          { title: "Mesai Bazlı Hatırlatıcılar", content: "Buradaki çalışma saatleri girilen personele, sistem sadece görev saatleri içindeki önemli bildirimleri iletir." }
        ]}
      />

      {modal && createPortal(
        <PersonelFormModal 
          personel={modal === 'yeni' ? null : modal} 
          personelTipleri={personelTipleri} 
          roller={roller} 
          onKaydet={handleKaydet} 
          onKapat={() => setModal(null)} 
          kaydediliyor={kaydediliyor} 
          hata={modalHata} 
        />,
        document.body
      )}

      {silOnay && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4 backdrop-blur-md animate-fade-in shadow-2xl leading-none font-black uppercase text-center">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 w-full max-w-sm border border-gray-100 leading-none">
            <div className="w-24 h-24 bg-rose-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-5xl shadow-xl shadow-rose-100/50 animate-bounce-subtle leading-none">🗑️</div>
            <h3 className="font-black text-gray-900 text-xl tracking-tight leading-none uppercase">Kaydı Sil</h3>
            <p className="text-[11px] font-black text-gray-400 mt-5 mb-10 leading-relaxed uppercase tracking-tight leading-loose italic">BU PERSONEL SİSTEMDEN KALICI OLARAK SİLİNECEKTİR. EMİN MİSİNİZ?</p>
            <div className="flex gap-4">
              <button onClick={() => setSilOnay(null)} className="flex-1 py-4 bg-gray-50 text-gray-600 rounded-2xl text-[10px] font-black hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95 leading-none">İPTAL</button>
              <button onClick={() => handleSil(silOnay)} className="flex-1 py-4 bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-100 transition-all uppercase tracking-widest transform-gpu active:scale-95 leading-none font-black text-lg">EVET, SİL</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
        .animate-shake { animation: shake 0.3s ease-in-out infinite; animation-iteration-count: 2; }
        @keyframes bounceSubtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .animate-bounce-subtle { animation: bounceSubtle 2s infinite; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
